import { useMemo, useState } from 'react'
import Modal from '@components/common/Modal'
import { useColumnAliases, useColumnAliasMutations } from '@hooks/useColumnAliases'
import { hasDrifted } from '@utils/applyColumnAliases'
import { toast } from '@store/toastStore'

/**
 * The firm-vocabulary editor (spec 061 §5). ONE implementation, deliberately.
 *
 * Jordan chose BOTH entry points on 2026-08-11 (O3): inline on the report, and
 * Settings → Reports. Two entry points sharing one editor and one mutation
 * path is the whole point — a second implementation of the same editor is
 * exactly how `ObjectLookup` and `LookupFilter` drifted into two pickers where
 * fixing one missed the other.
 *
 * ## What it will not do
 *
 * - It never writes a `columnId`. Renaming is cosmetic; the wire contract is
 *   not, and a misnamed filter key binds to nothing and runs the proc unscoped.
 * - A blank field is a RESET, not a label. cb2 never invents or prettifies a
 *   label; the only non-CB text ever shown is what an admin typed here.
 * - Aliases render as text nodes only — never `dangerouslySetInnerHTML`.
 *
 * ## Keyboard operability (WCAG 2.1.1)
 *
 * This is a custom control, so `aria-label` alone proves nothing. Every field
 * is a real `<input>` in DOM order, each row's reset is a real `<button>`, and
 * the modal shell supplies the focus trap, Escape and focus restore. Nothing
 * here is mouse-only.
 */
const ColumnAliasEditor = ({ reportKey, reportName, columns = [], onClose }) => {
  const { aliases, meta, canManage, permissionResolved, storageConfigured, loading } = useColumnAliases(reportKey)
  const { setAlias, resetAll } = useColumnAliasMutations(reportKey)

  // `draft` holds ONLY the columns the user is actively editing; everything
  // else reads straight from the server copy. Deliberately not seeded from
  // `aliases` in an effect: that needs a setState-in-effect (a cascading
  // render, and a lint error), and it goes stale the moment the OTHER entry
  // point renames something — the two entry points share one cache precisely
  // so that cannot happen. A key is dropped from the draft once its write
  // lands, handing that row back to the server value.
  const [draft, setDraft] = useState({})
  const valueFor = (columnId) => draft[columnId] ?? aliases[columnId] ?? ''
  const clearDraft = (columnId) => setDraft((d) => {
    const { [columnId]: _drop, ...rest } = d
    return rest
  })

  const rows = useMemo(
    () => columns.filter((c) => c && c.columnId != null && (c.rawLabel ?? c.label)),
    [columns]
  )

  // `value` is passed explicitly by the Reset button. Reading it off `draft`
  // there would take the PRE-clear value from this render's closure — React
  // state is async — and Reset would silently re-save the alias it just cleared.
  const commit = (col, value) => {
    const columnId = String(col.columnId)
    const next = (value ?? valueFor(columnId)).trim()
    const current = aliases[columnId] ?? ''
    if (next === current) { clearDraft(columnId); return }
    setAlias.mutate(
      { columnId, alias: next },
      {
        onSuccess: () => {
          // Hand the row back to the server copy, so what is on screen is what
          // is stored rather than what was typed.
          clearDraft(columnId)
          toast.success(next ? `Renamed to “${next}”` : 'Reset to the Centerbase label')
        },
        onError: (err) => {
          toast.error(err?.message || 'Could not save that name.')
          clearDraft(columnId) // never leave a lie on screen
        },
      }
    )
  }

  const cbLabel = (col) => col.rawLabel ?? col.label

  // Explain a failed permission check rather than silently hiding the editor —
  // an admin whose check failed would otherwise just see a missing feature.
  //
  // `loading` is checked FIRST and deliberately: canManage is false until the
  // read lands, so without this an admin is told "we could not confirm your
  // permissions" for a frame before the editor enables. Announcing a denial we
  // have not established is the same class of mistake as the queue modal
  // claiming a report had no filters.
  const blocked = loading
    ? 'Checking your permissions…'
    : !canManage
      ? (permissionResolved
          ? 'Renaming columns is limited to firm administrators.'
          : 'We could not confirm your permissions just now, so renaming is unavailable. Try again shortly.')
      : !storageConfigured
        ? 'Column renaming is not configured in this environment, so changes cannot be saved.'
        : null

  return (
    <Modal
      title="Rename columns"
      subtitle={reportName ? `${reportName} · the whole firm sees these names` : undefined}
      onClose={onClose}
      maxWidth="max-w-3xl"
      footer={(
        <>
          <span className="flex-1 text-[12.5px] text-gray-500">
            Leave a box empty to go back to the Centerbase name.
          </span>
          {canManage && !!Object.keys(aliases).length && (
            <button
              type="button"
              className="btn-cb-toolbar"
              disabled={resetAll.isPending}
              onClick={() => resetAll.mutate(undefined, {
                onSuccess: () => { setDraft({}); toast.success('All columns reset to Centerbase names') },
                onError: (err) => toast.error(err?.message || 'Could not reset this report.'),
              })}
            >
              Reset all
            </button>
          )}
          <button type="button" onClick={onClose} className="btn-cb-primary">Done</button>
        </>
      )}
    >
      <div className="px-5 py-4">
        {blocked && (
          <p className="mb-3.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-900">
            {blocked}
          </p>
        )}

        {/* The server export is written by Centerbase, so cb2 cannot inject the
            firm's words into it. Jordan's decision (O1, 2026-08-11) was to fix
            that properly in ExportHelper rather than accept it — so this notice
            is TEMPORARY and comes out when that backend change ships. Until
            then it must be stated: a firm sending a client a file that uses
            different words from the screen deserves to know why. */}
        <p className="mb-3.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-[11.7px] leading-relaxed text-gray-600">
          These names appear on screen and in the exports this page builds. The
          standard <b>Download</b> file is produced by Centerbase and still uses
          Centerbase&apos;s column names — that is being changed separately.
        </p>

        {rows.length === 0 ? (
          <p className="text-[12.5px] text-gray-500">This report has no columns to rename.</p>
        ) : (
          <table className="w-full" role="table">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th scope="col" className="pb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">Centerbase name</th>
                <th scope="col" className="pb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">Your firm&apos;s name</th>
                <th scope="col" className="pb-1.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((col) => {
                const columnId = String(col.columnId)
                const drifted = hasDrifted(col, meta[columnId])
                return (
                  <tr key={columnId} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-3 align-middle text-[12.5px] text-gray-700">
                      {cbLabel(col)}
                      {drifted && (
                        <span
                          className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-amber-900"
                          title={`Centerbase called this “${meta[columnId].original}” when it was renamed. It may now mean something different.`}
                        >
                          changed in Centerbase
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 align-middle">
                      {/* Plain ASCII on purpose: this string is an accessible
                          name that tests and tooling match on, and curly quotes
                          here have already survived one encoding round-trip
                          badly. Visible copy can use typographic quotes. */}
                      <label className="sr-only" htmlFor={`alias-${columnId}`}>
                        {`Firm name for the column: ${cbLabel(col)}`}
                      </label>
                      <input
                        id={`alias-${columnId}`}
                        type="text"
                        disabled={!!blocked || setAlias.isPending}
                        value={valueFor(columnId)}
                        placeholder={cbLabel(col)}
                        maxLength={60}
                        onChange={(e) => setDraft((d) => ({ ...d, [columnId]: e.target.value }))}
                        onBlur={() => commit(col)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() } }}
                        // 2026-08-26 ADA sweep: `.input-rc` in place of
                        // `border-gray-200 rounded-lg px-2.5 py-1.5
                        // text-[12.5px]`. #e5e7eb is 1.24:1, and on a
                        // white-on-white input the 1px border is the only thing
                        // identifying it — SC 1.4.11 wants 3.0:1, and
                        // --color-rc-control is 3.03:1. Geometry utilities are
                        // deleted, not paired: `.input-rc` is unlayered and
                        // would have made them dead code.
                        //
                        // `disabled:bg-gray-50` / `disabled:text-gray-500` went
                        // with them for the same reason — they lose to the
                        // class's own background-color and color. The disabled
                        // state is still drawn, by `.input-rc:disabled`
                        // (opacity 0.5 + not-allowed), which matters here
                        // because `blocked` genuinely disables every row.
                        className="w-full input-rc"
                      />
                    </td>
                    <td className="py-2 align-middle">
                      <button
                        type="button"
                        disabled={!!blocked || !valueFor(columnId)}
                        onClick={() => { setDraft((d) => ({ ...d, [columnId]: '' })); commit(col, '') }}
                        className="text-[11.5px] font-semibold text-cb-accent-text underline disabled:text-gray-400 disabled:no-underline"
                      >
                        Reset
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  )
}

export default ColumnAliasEditor
